// Analytics tracker (optional)
import { env } from '@config/env.js';

const META_OFFER_CHECKOUT_STORAGE_KEY = '5ugc_meta_offer_checkout';

class AnalyticsTracker {
  constructor() {
    this._initialized = false;
    this._measurementId = env.gtagMeasurementId;
    this._metaPixelId = env.metaPixelId;
    this._metaInitialized = false;
  }

  /**
   * Initialize analytics
   */
  init() {
    if (!this._measurementId || this._initialized || typeof window === 'undefined') return;

    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function gtag() {
      window.dataLayer.push(arguments);
    };
    window.__gtagInitializedIds = window.__gtagInitializedIds || {};

    if (!document.querySelector(`script[data-gtag-id="${this._measurementId}"]`)) {
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(this._measurementId)}`;
      script.dataset.gtagId = this._measurementId;
      document.head.appendChild(script);
    }

    if (!window.__gtagInitializedIds[this._measurementId]) {
      window.gtag('js', new Date());
      window.gtag('config', this._measurementId, { send_page_view: false });
      window.__gtagInitializedIds[this._measurementId] = true;
    }

    this._initialized = true;
    this._initMetaPixel();
  }

  /**
   * Track a page view
   * @param {string} pageName
   * @param {Object} properties
   */
  pageView(pageName, properties = {}) {
    if (!this._initialized) return;
    window.gtag?.('event', 'page_view', {
      page_title: document.title,
      page_location: window.location.href,
      page_path: pageName || `${window.location.pathname}${window.location.search}${window.location.hash}`,
      ...properties,
    });
  }

  /**
   * Track an event
   * @param {string} eventName
   * @param {Object} properties
   */
  event(eventName, properties = {}) {
    if (!this._initialized) return;
    window.gtag?.('event', eventName, properties);
  }

  /**
   * Identify a user
   * @param {string} userId
   * @param {Object} traits
   */
  identify(userId, traits = {}) {
    if (!this._initialized) return;
    window.gtag?.('set', { user_id: userId });
    if (Object.keys(traits).length > 0) {
      window.gtag?.('set', 'user_properties', traits);
    }
  }

  /**
   * Track anchor clicks across the app
   * @param {HTMLElement} link
   */
  trackLinkClick(link) {
    if (!this._initialized || !(link instanceof HTMLAnchorElement)) return;

    const href = link.getAttribute('href');
    if (!href || href.startsWith('javascript:')) return;

    let resolvedUrl;
    try {
      resolvedUrl = new URL(href, window.location.origin);
    } catch {
      return;
    }

    const linkText = (link.getAttribute('aria-label') || link.textContent || '')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 120);

    this.event('link_click', {
      link_text: linkText || '(no text)',
      link_url: resolvedUrl.href,
      link_path: `${resolvedUrl.pathname}${resolvedUrl.search}${resolvedUrl.hash}`,
      link_domain: resolvedUrl.hostname,
      link_type: resolvedUrl.origin === window.location.origin ? 'internal' : 'external',
    });
  }

  _initMetaPixel() {
    if (!this._metaPixelId || this._metaInitialized || typeof window === 'undefined') return;

    window.fbq = window.fbq || function fbqShim() {
      if (window.fbq.callMethod) {
        window.fbq.callMethod.apply(window.fbq, arguments);
      } else {
        window.fbq.queue.push(arguments);
      }
    };

    if (!window._fbq) {
      window._fbq = window.fbq;
    }

    window.fbq.push = window.fbq;
    window.fbq.loaded = true;
    window.fbq.version = '2.0';
    window.fbq.queue = window.fbq.queue || [];
    window.__metaPixelInitializedIds = window.__metaPixelInitializedIds || {};

    if (!document.querySelector(`script[data-meta-pixel-id="${this._metaPixelId}"]`)) {
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://connect.facebook.net/en_US/fbevents.js';
      script.dataset.metaPixelId = this._metaPixelId;
      document.head.appendChild(script);
    }

    if (!window.__metaPixelInitializedIds[this._metaPixelId]) {
      window.fbq('init', this._metaPixelId);
      window.__metaPixelInitializedIds[this._metaPixelId] = true;
    }

    this._metaInitialized = true;
  }

  _getCookie(name) {
    if (typeof document === 'undefined' || !name) return '';

    const match = document.cookie.match(new RegExp(`(?:^|; )${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : '';
  }

  _buildEventId(prefix = 'meta') {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return `${prefix}_${crypto.randomUUID()}`;
    }
    return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  }

  _readOfferCheckoutContext() {
    if (typeof sessionStorage === 'undefined') return null;

    try {
      const raw = sessionStorage.getItem(META_OFFER_CHECKOUT_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }

  _writeOfferCheckoutContext(context) {
    if (typeof sessionStorage === 'undefined') return;
    sessionStorage.setItem(META_OFFER_CHECKOUT_STORAGE_KEY, JSON.stringify(context));
  }

  _clearOfferCheckoutContext() {
    if (typeof sessionStorage === 'undefined') return;
    sessionStorage.removeItem(META_OFFER_CHECKOUT_STORAGE_KEY);
  }

  getMetaBrowserContext() {
    return {
      fbp: this._getCookie('_fbp'),
      fbc: this._getCookie('_fbc'),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    };
  }

  trackMeta(eventName, properties = {}, options = {}) {
    if (!this._metaInitialized || !eventName) return null;

    const eventId = typeof options.eventId === 'string' && options.eventId.trim().length > 0
      ? options.eventId.trim()
      : this._buildEventId(`meta_${String(eventName).toLowerCase()}`);
    const trackMethod = options.custom ? 'trackCustom' : 'track';
    const eventOptions = { eventID: eventId };

    window.fbq?.(trackMethod, eventName, properties, eventOptions);
    return eventId;
  }

  trackOfferViewContent({ contentName, contentCategory, locale, sourceUrl } = {}) {
    return this.trackMeta('ViewContent', {
      content_name: contentName || '5UGC Offer 1',
      content_category: contentCategory || 'offer',
      content_type: 'product',
      locale: locale || undefined,
      page_path: typeof window !== 'undefined' ? window.location.pathname : undefined,
      event_source_url: sourceUrl || (typeof window !== 'undefined' ? window.location.href : undefined),
    });
  }

  beginOfferCheckout({ sourceUrl, locale, value, currency } = {}) {
    const browserContext = this.getMetaBrowserContext();
    const context = {
      funnel: 'offer1',
      sourceUrl: sourceUrl || (typeof window !== 'undefined' ? window.location.href : ''),
      locale: locale || '',
      leadEventId: this._buildEventId('offer1_lead'),
      purchaseEventId: this._buildEventId('offer1_purchase'),
      value: Number.isFinite(Number(value)) ? Number(value) : undefined,
      currency: typeof currency === 'string' && currency.trim().length > 0 ? currency.trim().toUpperCase() : undefined,
      fbp: browserContext.fbp || undefined,
      fbc: browserContext.fbc || undefined,
      userAgent: browserContext.userAgent || undefined,
      createdAt: Date.now(),
    };

    this._writeOfferCheckoutContext(context);
    this.trackMeta('Lead', {
      content_name: '5UGC Offer 1',
      content_category: 'offer',
      locale: context.locale || undefined,
      event_source_url: context.sourceUrl || undefined,
    }, {
      eventId: context.leadEventId,
    });

    return context;
  }

  getOfferCheckoutContext() {
    return this._readOfferCheckoutContext();
  }

  trackOfferPurchaseFromSession({ sourceUrl } = {}) {
    const context = this._readOfferCheckoutContext();
    if (!context?.purchaseEventId) return false;

    this.trackMeta('Purchase', {
      content_name: '5UGC Offer 1',
      content_category: 'offer',
      content_type: 'product',
      value: context.value,
      currency: context.currency,
      locale: context.locale || undefined,
      event_source_url: sourceUrl || (typeof window !== 'undefined' ? window.location.href : context.sourceUrl),
    }, {
      eventId: context.purchaseEventId,
    });

    this._clearOfferCheckoutContext();
    return true;
  }
}

export const tracker = new AnalyticsTracker();
